import { FC, useState } from 'react';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { StatsDateFilter } from '../common/StatsDateFilter';
import { KpiCard } from '../common/KpiCard';
import { StatsBarChart } from '../common/StatsBarChart';
import { useUserActivity } from '../../../hooks/analytics/useUserActivity';
import RetentionSection from './RetentionSection';

const UserActivityPage: FC = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    const { data, isLoading, isError, error } = useUserActivity(startDate, endDate);

    const kpi = data?.kpi;
    const dauTrend = data?.dauTrend ?? [];
    const signupTrend = data?.signupTrend ?? [];
    const providerDistribution = data?.providerDistribution ?? [];
    const platformDistribution = data?.platformDistribution ?? [];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                사용자 활동 통계
            </Typography>

            {/* Date Filter */}
            <Box sx={{ mb: 3 }}>
                <StatsDateFilter onChange={handleDateChange} />
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}
                </Alert>
            )}

            {data && (
                <>
                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="DAU"
                                value={(kpi?.dau ?? 0).toLocaleString()}
                                changePercent={kpi?.dauChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="WAU"
                                value={(kpi?.wau ?? 0).toLocaleString()}
                                changePercent={kpi?.wauChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="MAU"
                                value={(kpi?.mau ?? 0).toLocaleString()}
                                changePercent={kpi?.mauChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="신규 가입"
                                value={(kpi?.newSignups ?? 0).toLocaleString()}
                                changePercent={kpi?.signupChange}
                            />
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    일별 활성 사용자 (DAU) 추이
                                </Typography>
                                <StatsBarChart
                                    data={dauTrend as unknown as Record<string, unknown>[]}
                                    series={[{ dataKey: 'count', name: 'DAU', color: '#1976d2' }]}
                                    xAxisKey="date"
                                />
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    일별 신규 가입 추이
                                </Typography>
                                <StatsBarChart
                                    data={signupTrend as unknown as Record<string, unknown>[]}
                                    series={[{ dataKey: 'count', name: '신규 가입', color: '#66bb6a' }]}
                                    xAxisKey="date"
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Distribution Tables */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    가입 경로별 분포
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>경로</TableCell>
                                                <TableCell align="right">수</TableCell>
                                                <TableCell align="right">비율</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {providerDistribution.map((row) => (
                                                <TableRow key={row.label}>
                                                    <TableCell>{row.label}</TableCell>
                                                    <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                                                    <TableCell align="right">{row.ratio.toFixed(1)}%</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    플랫폼별 분포
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>플랫폼</TableCell>
                                                <TableCell align="right">수</TableCell>
                                                <TableCell align="right">비율</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {platformDistribution.map((row) => (
                                                <TableRow key={row.label}>
                                                    <TableCell>{row.label}</TableCell>
                                                    <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                                                    <TableCell align="right">{row.ratio.toFixed(1)}%</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Retention Section */}
                    {startDate && endDate && <RetentionSection startDate={startDate} endDate={endDate} />}
                </>
            )}
        </Box>
    );
};

export default UserActivityPage;
